import React, { useEffect } from 'react';
import { Category, EventPageType } from '@graphql/generated';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation as useTranslationCore } from '@shared/core';
import { StyledRoot, styles } from './EventLogisticBlock.styles';
import { ViewDetailsArrow } from '@apps/common/components/ViewDetailsArrow/ViewDetailsArrow';
import { useShouldShowViewDetailsArrow } from '@apps/common/components/ViewDetailsArrow/useShouldShowViewDetailsArrow';
import { InlineEditor } from '@shared/components';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { display1ToLocationOverride, display2ToDateOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import useQueryString from '@shared/utils/hooks/useQueryString';
import { useFormattedDate } from '@localization/hooks/useFormattedDate';
import { useLocaleContext } from '@localization/context/LocaleContext';
import { addError } from '@shared/utils/logger';
import { DEFAULT_LANGUAGE } from '@shared/core/i18n/utils';
import { Skeleton } from '@shared/components/Skeleton';
import { useEventLocation } from '@shared/utils/eventLocation';
import { isInIframe } from '@shared/utils/isInIframe';
import { EventCountdown } from '@apps/common/components/EventCountdown/EventCountdown';
import type { EventLogisticsBlockProps } from './EventLogisticBlock.types';

// The original, non-localized function for getting the event date header
// preserved to ensure backwards compatibility
const _getEventDateHeaderDeprecated = (date: EventLogisticsBlockProps['date'], shouldBreak: boolean) => {
  let dateHeader = date ? date.dateString.replace(date.timeString, '').trim() : null;
  if (shouldBreak && dateHeader) {
    const [dayOfWeek, ...dates] = dateHeader.split(',');
    dateHeader = `${dayOfWeek}\n${dates.join(',')}`;
  }
  return dateHeader;
};

const useEventDateHeader = (date: EventLogisticsBlockProps['date'], shouldBreak: boolean) => {
  const { language } = useLocaleContext();

  const deprecatedDateHeader = _getEventDateHeaderDeprecated(date, shouldBreak);
  const localizedDateHeader =
    useFormattedDate(date?.timestamp, shouldBreak ? 'weekday\ndd MMMM yyyy' : 'weekday, dd MMMM yyyy', { titlecase: true, timezone: date?.timezone }) ?? null;

  useEffect(() => {
    if (language === DEFAULT_LANGUAGE && deprecatedDateHeader !== localizedDateHeader) {
      addError('Localized date mismatch', { context: 'useEventDateHeader', deprecatedDateHeader, localizedDateHeader });
    }
  }, [language, deprecatedDateHeader, localizedDateHeader]);

  return localizedDateHeader;
};

const EventLogisticsBlock: React.FC<EventLogisticsBlockProps> = ({
  children,
  page,
  eventHandle,
  location,
  date,
  shouldBreakDate = false,
  hideEventCountdown,
  showViewDetailsButton,
  onViewDetailsButtonClicked,
  pages,
  ...restProps
}) => {
  const inlineEditorTransFn = useTranslationCore('joykit').t('inlineEditor');

  const eventDateHeader = useEventDateHeader(date, shouldBreakDate);
  const queryString = useQueryString();
  const { shouldShow: shouldShowViewDetailsArrow, nextPageSlug } = useShouldShowViewDetailsArrow(pages);

  // Use admin mode in preview iframe so admins can see draft/unpublished venue updates
  const isPreviewing = isInIframe();
  const { eventLocationDisplayName, isLoadingEventLocation } = useEventLocation({ locationName: location, isAdmin: isPreviewing });

  const handleEditEventDateClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editDate',
        inlineEditData: { date: date?.dateString || '', timeZone: date?.timezone || 'America/Los_Angeles', label: inlineEditorTransFn.eventDate() },
      },
    });
  });

  const handleEditEventLocationClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editLocation', inlineEditData: { location: location || '', label: inlineEditorTransFn.eventLocation() } },
    });
  });

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } },
    });
  });

  if (!children && !location && !date) {
    return null;
  }

  return (
    // position relative should prevent the back sprite from being above the z of this box
    <StyledRoot
      __css={styles.root}
      {...restProps}
    >
      <Flex
        flexDirection={'column'}
        textAlign={'center'}
      >
        {eventDateHeader && (
          <InlineEditor
            elementLabel={inlineEditorTransFn.eventDate()}
            actionData={{
              edit: handleEditEventDateClick,
              font: () => handleEditFontClick(Category.DATE),
            }}
            componentName="eventDate"
            pageName={EventPageType.welcome}
            pageSlug="welcome"
            wrapperCSS={{ zIndex: 100 }}
          >
            <GuestSiteTypographyOverride override={display2ToDateOverride}>
              <TextV2
                data-testid={'logistics-date'}
                typographyVariant={'display2'}
                whiteSpace={'pre-wrap'}
              >
                {eventDateHeader}
              </TextV2>
            </GuestSiteTypographyOverride>
          </InlineEditor>
        )}
        {location && (
          <Box marginTop={5}>
            <InlineEditor
              elementLabel={inlineEditorTransFn.eventLocation()}
              actionData={{
                edit: handleEditEventLocationClick,
                font: () => handleEditFontClick(Category.LOCATION),
              }}
              componentName="eventLocation"
              pageName={EventPageType.welcome}
              pageSlug="welcome"
              wrapperCSS={{ zIndex: 100 }}
            >
              <GuestSiteTypographyOverride override={display1ToLocationOverride}>
                {isLoadingEventLocation ? (
                  <Skeleton
                    data-testid={'logistics-location'}
                    placeholder={<Box typographyVariant={'display1'}>&nbsp;</Box>}
                  />
                ) : (
                  <TextV2
                    data-testid={'logistics-location'}
                    typographyVariant={'display1'}
                  >
                    {eventLocationDisplayName}
                  </TextV2>
                )}
              </GuestSiteTypographyOverride>
            </InlineEditor>
          </Box>
        )}
        {!hideEventCountdown && date && (
          <EventCountdown
            data-testid={'logistics-countdown'}
            milliseconds={date.milliseconds}
            timezone={date.timezone}
            marginTop={6}
          />
        )}
      </Flex>
      {children && <>{children}</>}
      {/* View Details + Arrow */}
      {shouldShowViewDetailsArrow && nextPageSlug && eventHandle ? (
        <ViewDetailsArrow
          show={!!showViewDetailsButton}
          eventHandle={eventHandle}
          nextPageSlug={nextPageSlug}
          queryString={queryString}
          onViewDetailsButtonClicked={onViewDetailsButtonClicked}
        />
      ) : null}
    </StyledRoot>
  );
};

EventLogisticsBlock.displayName = 'EventLogisticsBlock';

export { EventLogisticsBlock };
